import {NgTemplateOutlet} from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChild,
  input,
  signal,
  TemplateRef,
  ViewEncapsulation,
} from '@angular/core';

/**
 * Component for handling media previews with loading states and error handling.
 * Supports template-based default and broken image fallbacks via content projection.
 *
 * @example
 * ```typescript
 * @Component({
 *   imports: [MediaPreviewComponent],
 *   template: `
 *     <media-preview [src]="imageUrl" [spinner]="true" class="w-10 h-10 rounded-full">
 *       <ng-template #default>
 *         <default-avatar></default-avatar>
 *       </ng-template>
 *     </media-preview>
 *   `
 * })
 * export class ImageComponent {
 *   imageUrl = 'https://example.com/image.jpg';
 * }
 * ```
 */
@Component({
  selector: 'media-preview',
  standalone: true,
  imports: [NgTemplateOutlet],
  template: `
    @if (showImage()) {
      <img [src]="src()" (load)="onImageLoad()" (error)="onImageError()" alt="" class="h-full w-full object-cover" />
    } @else if (showDefault()) {
      @if (defaultTemplate(); as template) {
        <ng-container *ngTemplateOutlet="template"></ng-container>
      }
    } @else if (showBroken()) {
      @if (brokenTemplate(); as template) {
        <ng-container *ngTemplateOutlet="template"></ng-container>
      }
    }

    @if (showSpinner()) {
      <div class="absolute inset-0 flex items-center justify-center">
        <div class="h-6 w-6 animate-spin rounded-full border-b-2 border-current"></div>
      </div>
    }
  `,
  styles: `
    :host {
      display: inline-block;
      position: relative;
      overflow: hidden;
    }
  `,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaPreviewComponent {
  src = input<string>();
  spinner = input<boolean>(true);

  defaultTemplate = contentChild('default', {read: TemplateRef});
  brokenTemplate = contentChild('broken', {read: TemplateRef});

  private imageLoaded = signal(false);
  private imageError = signal(false);

  showSpinner = computed(() => this.spinner() && !this.imageLoaded() && !this.imageError() && !!this.src());
  showImage = computed(() => !!this.src() && !this.imageError());
  showDefault = computed(() => !this.src() && !this.imageError());
  showBroken = computed(() => this.imageError());

  onImageLoad() {
    this.imageLoaded.set(true);
  }

  onImageError() {
    this.imageError.set(true);
  }
}
